import React, { useCallback, useEffect, useMemo } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Box } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { GenericModalMessageTypes } from '@europrocurement/flexy-components/types/GenericModalMessageTypes';
import {
    DocumentItemDetailData,
    MessageItemDetailData,
    useModal,
    useUploader,
} from '@europrocurement/flexy-components';
import { FlexyForm, FormObject } from '@europrocurement/flexy-form';
import { markDownToHtml } from '@europrocurement/l2d-utils';
import {
    useWebsocketActions,
    useWebsocketState,
    type WebsocketPayloadMessage,
} from '@europrocurement/l2d-websocket';
import {
    ApiFichiersPostExpediteurEnum,
    ApiFichiersPostExpeditionEnum,
} from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { UseKeycloakService } from '@europrocurement/l2d-keycloak';
import { STATUS_VALID } from '../../../providers';
import useFormStructureGenericModal from '../hooks/useFormStructureGenericModal';
import useDossierDocumentFilesApi from '../../Dossiers/hooks/useDossierDocumentFilesApi';
import useDossierFile from '../../Dossiers/hooks/useDossierFile';

const GenericModalMessageContent: React.FunctionComponent<GenericModalMessageTypes> = function ({
    body,
    entity,
    bodyinputSx = {},
    getFormContext,
}) {
    const formContext = useForm({
        mode: 'onTouched',
        defaultValues: {
            messageBody: body || '',
        } as FormObject,
    });

    const dossier = entity;
    /**
     * Prevent form submission when Enter key is pressed
     */
    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLFormElement>) => {
        if (e.key === 'Enter' && e.ctrlKey === false) {
            e.preventDefault();
        }
    }, []);
    const [submittingForm, setSubmittingForm] = React.useState(false);

    const { getFormStructure } = useFormStructureGenericModal({
        body,
        entity: dossier,
        submittingForm,
    });
    const { modalActions } = useModal();
    const { files } = useUploader();
    const { socketOpen } = useWebsocketState();
    const { sendMessage } = useWebsocketActions();
    const keycloakService = UseKeycloakService();
    const keycloakInstance = keycloakService.getKc();
    const userInfo = keycloakService.getTokenParsed();
    const isInterne = userInfo?.realm_access?.roles.includes('interne');
    const expediteur = isInterne
        ? ApiFichiersPostExpediteurEnum.NUMBER_3
        : ApiFichiersPostExpediteurEnum.NUMBER_1;
    const topicId = useMemo(
        () => (dossier?.id ? `messagerie_dossier_formalite_${dossier.id}` : null),
        [dossier?.id],
    );
    const { uploadAdditionnalDocDosFile } = useDossierDocumentFilesApi();
    const { handleUpdateDosDocFile } = useDossierFile({});
    const sendWebSocketMessage = useCallback(
        async (
            messageSend: MessageItemDetailData | DocumentItemDetailData,
            type: 'messagerie_dossier' | 'notification',
        ) => {
            const token = await keycloakInstance
                .updateToken(10)
                .then(() => keycloakInstance.token || '');

            if (token === undefined) {
                enqueueSnackbar('Vous devez être connecté pour envoyer un message', {
                    variant: 'error',
                });
                return;
            }
            if (!topicId) {
                enqueueSnackbar('Aucun dossier sélectionné', { variant: 'error' });
                return;
            }
            const payload: WebsocketPayloadMessage = {
                payload: {
                    token,
                    topicType: type,
                    topicId,
                    userIdentifier: keycloakService.getTokenParsed()?.preferred_username,
                    date: new Date().toISOString(),
                    discriminant: {
                        idPrescripteur: dossier.prescripteurId ?? 0,
                        idDossierFormalite: dossier.id ?? 0,
                        userIdentifierFormaliste: '',
                        idFormaliste: dossier.formalisteId ?? 0,
                        idFormalite: dossier.formaliteId ?? 0,
                        idClient: dossier.clientId ?? 0,
                    },
                    message: messageSend,
                },
            };

            if (socketOpen) {
                await sendMessage(payload);
                enqueueSnackbar('Message envoyé avec succès', { variant: 'success' });
            }
        },
        [
            sendMessage,
            topicId,
            dossier.clientId,
            dossier.formalisteId,
            dossier.formaliteId,
            dossier.id,
            dossier.prescripteurId,
            keycloakInstance,
            keycloakService,
            socketOpen,
        ],
    );
    const handleUploadFile = useCallback(
        async (file: File) => {
            try {
                if (!dossier.id) {
                    enqueueSnackbar('Dossier non trouvé', { variant: 'error' });
                    return;
                }
                const response = await uploadAdditionnalDocDosFile(
                    dossier.id,
                    file,
                    expediteur,
                    ApiFichiersPostExpeditionEnum.NUMBER_6,
                    false,
                );

                if (!response) throw new Error('Upload failed');
                if (response.files) {
                    response.files.forEach(async (fichier) => {
                        if (!fichier.id || !fichier.fichier || !fichier.contentUrl) {
                            enqueueSnackbar('Réponse du serveur invalide', { variant: 'error' });
                            return;
                        }

                        const { success } = await handleUpdateDosDocFile(fichier.id, {
                            status: STATUS_VALID,
                        });

                        if (!success) {
                            return;
                        }

                        const dosDocFileId = fichier.id;
                        const dosDocFileName = fichier.fichier;
                        const dosDocFileUrl = fichier.contentUrl;
                        await sendWebSocketMessage(
                            {
                                type: 'document',
                                dosDocFileId,
                                dosDocFileName,
                                dosDocFileUrl,
                            } as DocumentItemDetailData,
                            'messagerie_dossier',
                        );
                    });
                }
            } catch (err) {
                enqueueSnackbar(`Une erreur est survenue "${err}"`, { variant: 'error' });
            }
        },
        [
            dossier.id,
            expediteur,
            uploadAdditionnalDocDosFile,
            handleUpdateDosDocFile,
            sendWebSocketMessage,
        ],
    );
    const handleSubmit = useCallback<SubmitHandler<{ messageBody: string | undefined }>>(
        async (values, event) => {
            setSubmittingForm(true);

            const submitter = (event?.nativeEvent as SubmitEvent)?.submitter as HTMLButtonElement;
            let messageBody = body;
            if (values.messageBody) {
                messageBody = values.messageBody as string;
            }
            if (messageBody) {
                messageBody = markDownToHtml({ content: messageBody });
            }
            if (submitter?.name === 'sendmessageandnotification') {
                await sendWebSocketMessage(
                    {
                        type: 'message',
                        message: `Vous avez un nouveau message sur votre dossier : ${dossier.collectionNumero ?? ''}`,
                    } as MessageItemDetailData,
                    'notification',
                );
            }
            await sendWebSocketMessage(
                {
                    type: 'message',
                    message: messageBody || '',
                },
                'messagerie_dossier',
            );
            if (files && files.length > 0) {
                await Promise.all(files.map((file) => handleUploadFile(file)));
            }
            setSubmittingForm(false);
            modalActions.close();
        },
        [
            body,
            files,
            handleUploadFile,
            sendWebSocketMessage,
            modalActions,
            dossier.collectionNumero,
        ],
    );

    const formProps = {
        formContext,
        formObject: {},
        onkeydown: handleKeyDown,
        onSubmit: handleSubmit,
        gridProps: { sx: { gap: '8px' } },
        submitButton: {
            label: 'Envoyer',
            displayed: false,
        },
        isSubmitting: submittingForm,
    };

    useEffect(() => {
        if (typeof getFormContext === 'function') {
            getFormContext({
                getMessageBody: () => formContext.getValues('messageBody') as string | undefined,
                setMessageBody: (value: string) => formContext.setValue('messageBody', value),
            });
        }
    }, [getFormContext, formContext]);

    return (
        <Box sx={{ minWidth: '20rem', maxWidth: '40rem', ...bodyinputSx }}>
            <FlexyForm
                formStructure={getFormStructure()}
                {...formProps}
            />
        </Box>
    );
};

export default GenericModalMessageContent;
